import { toApiError } from '@/lib/http';

/**
 * The khutbah's learning content: every word with its gloss, and the grammar
 * notes.
 *
 * Fetched for the whole khutbah at once. The reader draws twenty passages,
 * and asking per passage would be twenty round trips to show one screen.
 */

export interface GlossedWord {
  readonly ordinal: number;
  /** Exactly as the khutbah wrote it, tashkeel intact. */
  readonly surface: string;
  /** `none` when this language has no meaning for it yet — the normal case. */
  readonly state: 'none' | 'glossed';
  readonly lemma: string | null;
  /** Letters spaced, never joined: ت ق و. */
  readonly root: string | null;
  readonly partOfSpeech: string | null;
  readonly transliteration: string | null;
  readonly meaning: string | null;
  readonly note: string | null;
  readonly reviewed: boolean;
}

export interface GrammarNote {
  readonly passageId: string;
  readonly heading: string;
  readonly body: string;
  readonly pattern: string | null;
  readonly reviewed: boolean;
}

export interface Quiz {
  readonly passageId: string;
  readonly kind: 'multiple_choice' | 'fill_blank' | 'ordering';
  readonly question: string;
  readonly hint: string | null;
  /** Shape depends on the kind, so it stays JSON until the quiz renders it. */
  readonly answer: unknown;
  readonly reviewed: boolean;
}

export interface LearningContent {
  readonly language: string;
  readonly byPassage: ReadonlyMap<string, readonly GlossedWord[]>;
  readonly notes: readonly GrammarNote[];
  readonly quizzes: readonly Quiz[];
  readonly glossed: number;
  readonly words: number;
}

function str(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

export function parseLearningContent(value: unknown): LearningContent {
  const source = (typeof value === 'object' && value !== null ? value : {}) as Record<
    string,
    unknown
  >;
  const passages = Array.isArray(source.passages) ? source.passages : [];
  const byPassage = new Map<string, readonly GlossedWord[]>();

  for (const entry of passages) {
    if (typeof entry !== 'object' || entry === null) continue;
    const passage = entry as Record<string, unknown>;
    const passageId = str(passage, 'passageId');
    if (passageId === null) continue;
    const words = Array.isArray(passage.words) ? passage.words : [];
    byPassage.set(
      passageId,
      words.flatMap((wordValue): GlossedWord[] => {
        if (typeof wordValue !== 'object' || wordValue === null) return [];
        const word = wordValue as Record<string, unknown>;
        const surface = str(word, 'surface');
        if (surface === null) return [];
        return [
          {
            ordinal: typeof word.ordinal === 'number' ? word.ordinal : 0,
            surface,
            // Defaults to 'none': claiming a word is glossed when the server
            // did not say so would open an empty popover on tap.
            state: word.state === 'glossed' ? 'glossed' : 'none',
            lemma: str(word, 'lemma'),
            root: str(word, 'root'),
            partOfSpeech: str(word, 'partOfSpeech'),
            transliteration: str(word, 'transliteration'),
            meaning: str(word, 'meaning'),
            note: str(word, 'note'),
            reviewed: word.reviewed === true,
          },
        ];
      }),
    );
  }

  const coverage = (
    typeof source.coverage === 'object' && source.coverage !== null
      ? source.coverage
      : {}
  ) as Record<string, unknown>;

  return {
    language: str(source, 'language') ?? '',
    byPassage,
    notes: (Array.isArray(source.notes) ? source.notes : []).flatMap(
      (entry): GrammarNote[] => {
        if (typeof entry !== 'object' || entry === null) return [];
        const note = entry as Record<string, unknown>;
        const passageId = str(note, 'passageId');
        const heading = str(note, 'heading');
        if (passageId === null || heading === null) return [];
        return [
          {
            passageId,
            heading,
            body: str(note, 'body') ?? '',
            pattern: str(note, 'pattern'),
            reviewed: note.reviewed === true,
          },
        ];
      },
    ),
    quizzes: (Array.isArray(source.quizzes) ? source.quizzes : []).flatMap(
      (entry): Quiz[] => {
        if (typeof entry !== 'object' || entry === null) return [];
        const quiz = entry as Record<string, unknown>;
        const question = str(quiz, 'question');
        const passageId = str(quiz, 'passageId');
        if (question === null || passageId === null) return [];
        const kind = quiz.kind;
        return [
          {
            passageId,
            kind:
              kind === 'fill_blank' || kind === 'ordering' ? kind : 'multiple_choice',
            question,
            hint: str(quiz, 'hint'),
            // Left as JSON: each kind reads it differently, and parsing it
            // here would mean knowing all three shapes in one place.
            answer: (() => {
              const raw = str(quiz, 'answer');
              if (raw === null) return null;
              try {
                return JSON.parse(raw) as unknown;
              } catch {
                return null;
              }
            })(),
            reviewed: quiz.reviewed === true,
          },
        ];
      },
    ),
    glossed: typeof coverage.glossed === 'number' ? coverage.glossed : 0,
    words: typeof coverage.words === 'number' ? coverage.words : 0,
  };
}

export async function fetchLearning(
  khutbahId: string,
  language: string,
  signal: AbortSignal,
): Promise<LearningContent> {
  const response = await fetch(
    `/api/public/khutbah/${encodeURIComponent(khutbahId)}/learning?language=${encodeURIComponent(language)}`,
    { signal, headers: { Accept: 'application/json' } },
  );
  if (!response.ok) {
    throw await toApiError(response);
  }
  return parseLearningContent(await response.json());
}
